import { PartialQueryVariables, TagsQuery, TagsQuery_tagsConnection_nodes, TagsQueryGQL, TagsQueryVariables } from '@tableau/data-catalog-client-components';
import { defaultMemoize } from 'reselect';
import { ListResult } from '../../ts/fetchers/ListResult';
import { IFieldValue } from '../../ts/ServerApi/types/IFieldValue';
import { ILeafFilterClause } from '../../ts/ServerApi/types/IFilterClause';
import { catalogApolloClient } from '../CatalogApolloClient';
import { GraphQLSliceableFactory, makeSliceableFactoryForGraphQLQuery } from './GraphQLSliceable';

type FieldValueResultItem = IFieldValue & { id: string };

export type FieldValueContentType = 'database' | 'table';

const getSliceableFactoryForExternalContentTags = defaultMemoize((contentType: FieldValueContentType) => {
  return makeSliceableFactoryForGraphQLQuery<
    TagsQuery_tagsConnection_nodes,
    FieldValueResultItem,
    TagsQueryVariables,
    TagsQuery
    >(
    {
      queryGql: TagsQueryGQL,
      selectConnectionListResult: results => results && results.tagsConnection,
      mapResultNode: (tag, _) => ({
        label: tag.name,
        value: tag.name,
        count: contentType === 'table' ? tag.databaseTablesConnection.totalCount : tag.databasesConnection.totalCount,
        id: tag.id
      }),
    },
    catalogApolloClient
  );
});

export class GraphQLTagValuesQuery {

  private filterClauses: ILeafFilterClause[];
  private matchText: string;
  private sliceableFactory: GraphQLSliceableFactory<TagsQueryVariables, FieldValueResultItem>;

  constructor(
    private contentType: FieldValueContentType,
    ) {
      this.sliceableFactory = getSliceableFactoryForExternalContentTags(contentType);
    }

  public getSlice(start: number, size: number): ng.IPromise<ListResult<IFieldValue>> {

    const partialQueryVariables: PartialQueryVariables<TagsQueryVariables> = {
      name: this.matchText === '' ? null : this.matchText
    };

    const sliceable = this.sliceableFactory(partialQueryVariables);

    return sliceable.getSlice(start, size);
  }

  public getFilters(): ILeafFilterClause[] {
    return this.filterClauses;
  }

  public withMatchText(matchText: string): GraphQLTagValuesQuery {
    return this.clone().setMatchText(matchText);
  }

  public withFilters(filterClauses: ILeafFilterClause[]): GraphQLTagValuesQuery {
    return this.clone().setFilterClauses(filterClauses);
  }

  private clone(): GraphQLTagValuesQuery {
    return new GraphQLTagValuesQuery(this.contentType)
      .setFilterClauses(this.filterClauses)
      .setMatchText(this.matchText);
  }

  private setFilterClauses(filterClauses: ILeafFilterClause[]): GraphQLTagValuesQuery {
    this.filterClauses = filterClauses;
    return this;
  }

  private setMatchText(matchText: string): GraphQLTagValuesQuery {
    this.matchText = matchText;
    return this;
  }

}
